import { debounce, set } from "lodash";
import * as React from "react";
import * as AccountStore from "../account/AccountStore";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import * as IdentityPermissionStore from "../identityPermissions/IdentityPermissionStore";
import OrganizationContextSwitcher from "../organizations/ContextSwitcher";
import * as UserStore from "./UserStore";
import { UserDetailsModal } from "./UserDetailsModal";
import { UserLifecycleAction, UserLifecycleModal } from "./UserLifecycleModal";
import { UsersList } from "./UsersList";
import { UsersToolbar } from "./UsersToolbar";
import { useUserSearch } from "./useUserSearch";
import { ImportUsersModal } from "./ImportUsersModal";
import { UserFormModal } from "./UserFormModal";
import { UserActionModals } from "./UserActionModals";
import { RelatedUsersModal } from "./RelatedUsersModal";
import { AddDelegationModal } from "./AddDelegationModal";

export const UsersIndex = () => {
    const [searchQuery, setSearchQuery] = React.useState("");
    const [selectedUser, setSelectedUser] = React.useState({} as AccountStore.Account);
    const [selectedUserMetadata, setSelectedUserMetadata] = React.useState({});

    const [canViewMetadata, setCanViewMetadata] = React.useState(false);
    const [canImportUsers, setCanImportUsers] = React.useState(false);
    const [canCreateUsers, setCanCreateUsers] = React.useState(false);
    const [canImpersonate, setCanImpersonate] = React.useState(false);
    const [canSendMessage, setCanSendMessage] = React.useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);
    const [canEditUser, setCanEditUser] = React.useState(false);
    const [canManageMemberships, setCanManageMemberships] = React.useState(false);
    const [canManageUserPermissions, setCanManageUserPermissions] = React.useState(false);

    const [organizationId, setOrganizationId] = React.useState("");
    const [canResetPassword, setCanResetPassword] = React.useState(false);

    const [showMetadataModal, setShowMetadataModal] = React.useState(false);
    const [showImportUsersModal, setShowImportUsersModal] = React.useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = React.useState(false);
    const [showUpdateUserModal, setShowUpdateUserModal] = React.useState(false);
    const [showRelatedUsersModal, setShowRelatedUsersModal] = React.useState(false);

    const [showCreateMessageModal, setShowCreateMessageModal] = React.useState(false);
    const [lifecycleAction, setLifecycleAction] = React.useState<UserLifecycleAction | null>(null);
    const [lifecycleUser, setLifecycleUser] = React.useState(null as any);

    const [relatedUsers, setRelatedUsers] = React.useState([] as any[]);
    const [selectedMetadataKey, setSelectedMetadataKey] = React.useState("");
    const [selectedMetadataValue, setSelectedMetadataValue] = React.useState("");
    
    const [includeStaff, setIncludeStaff] = React.useState(true);
    const [includeStudents, setIncludeStudents] = React.useState(true);
    const [includeParents, setIncludeParents] = React.useState(true);
    const [includeGuests, setIncludeGuests] = React.useState(true);

    const searchFilters = React.useMemo(() => ({
        includeStaff,
        includeStudents,
        includeParents,
        includeGuests,
    }), [includeStaff, includeStudents, includeParents, includeGuests]);
    const { users, isLoading: isSearching, error: searchError, refresh: refreshUsers } = useUserSearch(organizationId, searchQuery, searchFilters);
    const detailsRequestVersion = React.useRef(0);
    const relatedRequestVersion = React.useRef(0);

    const showMetadata = (user) => () => {
        const version = ++detailsRequestVersion.current;
        UserStore.GetUser(user.id).then((response) => {
            if (detailsRequestVersion.current !== version) return;
            var userMetadata = {};

            (response.data.metadata || []).forEach((m) => {
                if (m.enableOverride) {
                    set(userMetadata, m.key, m.overrideValue);
                } else {
                    set(userMetadata, m.key, m.value);
                }
            });

            setSelectedUser(response.data);
            setShowMetadataModal(true);
            setSelectedUserMetadata(userMetadata);
            
            // Load delegated access data if user has permission to manage it
            if (canManageUserPermissions) {
                loadDelegatedAccess(user.id);
            }
        });
    };

    const toggleMetadataModal = () => {
        detailsRequestVersion.current += 1;
        setShowMetadataModal(false);
    };

    const toggleImportUsersModal = () => {
        setShowImportUsersModal((isOpen) => !isOpen);
    };

    React.useEffect(() => {
        const contextSubscriptionId = GetContext((context) => {
            setOrganizationId(context.currentOrganization.id);
            IdentityPermissionStore.CheckCurrentUserHasPermission("getUser").then((result) => {
                setCanViewMetadata(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("importUsersFromExcel").then((result) => {
                setCanImportUsers(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("createUser").then((result) => {
                setCanCreateUsers(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("resetPassword").then((result) => {
                setCanResetPassword(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("updateUser").then((result) => {
                setCanEditUser(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("sendSingleMessage").then((result) => {
                setCanSendMessage(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("impersonateUsers").then((result) => {
                AccountStore.GetCurrentLoggedInUser().then((response) => {
                    if (response.claims.some((c) => c.type === AccountStore.ROLE_CLAIM && c.value === "SuperAdmin")) {
                        setCanImpersonate(result);
                        setIsSuperAdmin(true);
                    }
                });
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("manageUserPermissions").then((result) => {
                setCanManageUserPermissions(result);
            });
            IdentityPermissionStore.CheckCurrentUserHasPermission("manageOrganizationMembership").then((result) => {
                setCanManageMemberships(result);
            });
        });
        return () => {
            UnsubscribeContext(contextSubscriptionId);
        };
    }, []);

    const startResetPassword = (user) => () => {
        setShowResetPasswordModal(true);
        setSelectedUser(user);
    };

    const startSendMessage = (user) => () => {
        setShowCreateMessageModal(true);
        setSelectedUser(user);
    };

    const [showLoginLinkModal, setShowLoginLinkModal] = React.useState(false);

    const startLoginLink = (user) => () => {
        setShowLoginLinkModal(true);
        setSelectedUser(user);
    };

    const [showCreateUserModal, setShowCreateUserModal] = React.useState(false);

    const toggleCreateUserModal = () => {
        setShowCreateUserModal((isOpen) => !isOpen);
    };

    const startUpdateUser = (user) => () => {
        setShowUpdateUserModal(true);
        setSelectedUser(user);
    };

    const [showManageClaimsModal, setShowManageClaimsModal] = React.useState(false);

    // Delegated Access State
    const [delegatedAccessData, setDelegatedAccessData] = React.useState({} as UserStore.DelegatedAccessResponse);
    const [showAddDelegationModal, setShowAddDelegationModal] = React.useState(false);
    const [delegationSearchQuery, setDelegationSearchQuery] = React.useState("");
    const [delegationSearchResults, setDelegationSearchResults] = React.useState([] as any[]);
    const [newDelegationRules, setNewDelegationRules] = React.useState("");
    const [selectedDelegatee, setSelectedDelegatee] = React.useState(null as any);

    const startManageClaims = (user) => () => {
        setShowManageClaimsModal(true);
        setSelectedUser(user);
    };

    const startShowRelated = (user, metadataKey, metadataValue) => () => {
        const version = ++relatedRequestVersion.current;
        setSelectedUser(user);
        setSelectedMetadataKey(metadataKey);
        setSelectedMetadataValue(metadataValue);

        UserStore.SearchUsersByMetadata(metadataKey, metadataValue)
            .then((response) => {
                if (relatedRequestVersion.current !== version) return;
                setRelatedUsers(response.data);
                setShowRelatedUsersModal(true);
            })
            .catch((error) => {
                if (relatedRequestVersion.current !== version) return;
                console.error("Error searching related users:", error);
                setRelatedUsers([]);
                setShowRelatedUsersModal(true);
            });
    };

    const toggleRelatedUsersModal = () => {
        relatedRequestVersion.current += 1;
        setShowRelatedUsersModal(false);
    };

    const startLifecycleAction = (action: UserLifecycleAction, user: any) => {
        setLifecycleUser(user);
        setLifecycleAction(action);
    };

    const closeLifecycleModal = () => {
        setLifecycleAction(null);
        setLifecycleUser(null);
    };

    const completeLifecycleAction = () => {
        closeLifecycleModal();
        refreshUsers();
    };

    // Delegated Access Functions
    const delegationDataRequestVersion = React.useRef(0);
    const loadDelegatedAccess = (userId: string) => {
        const version = ++delegationDataRequestVersion.current;
        UserStore.GetUserDelegatedAccess(userId).then((response) => {
            if (delegationDataRequestVersion.current !== version) return;
            setDelegatedAccessData(response.data);
        }).catch((error) => {
            if (delegationDataRequestVersion.current !== version) return;
            console.error("Error loading delegated access:", error);
            setDelegatedAccessData({ hasGivenAccessTo: [], hasAccessOn: [] });
        });
    };

    const delegationRequestVersion = React.useRef(0);
    const searchUsersForDelegation = React.useCallback((query: string) => {
        const version = ++delegationRequestVersion.current;
        if (query.length > 2) {
            UserStore.SearchUsers(query, 0, 10, true, true, true, true).then((response) => {
                if (delegationRequestVersion.current !== version) return;
                setDelegationSearchResults(response.data.filter((u: any) => u.id !== selectedUser.id));
            });
        } else {
            setDelegationSearchResults([]);
        }
    }, [selectedUser.id]);

    const debouncedSearchUsersForDelegation = React.useMemo(() => debounce(searchUsersForDelegation, 300), [searchUsersForDelegation]);

    const startAddDelegation = () => {
        setShowAddDelegationModal(true);
        setDelegationSearchQuery("");
        setDelegationSearchResults([]);
        setNewDelegationRules("");
        setSelectedDelegatee(null);
    };

    const addDelegation = () => {
        if (selectedDelegatee) {
            UserStore.AddDelegatedAccess(selectedUser.id, selectedDelegatee.id, newDelegationRules)
                .then((response) => {
                    if (response.data.success) {
                        loadDelegatedAccess(selectedUser.id);
                        setShowAddDelegationModal(false);
                        setDelegationSearchQuery("");
                        setDelegationSearchResults([]);
                        setNewDelegationRules("");
                        setSelectedDelegatee(null);
                    }
                })
                .catch((error) => {
                    console.error("Error adding delegation:", error);
                });
        }
    };

    const removeDelegation = (delegateeId: string) => {
        UserStore.RemoveDelegatedAccess(selectedUser.id, delegateeId)
            .then((response) => {
                if (response.data.success) {
                    loadDelegatedAccess(selectedUser.id);
                }
            })
            .catch((error) => {
                console.error("Error removing delegation:", error);
            });
    };

    React.useEffect(() => {
        debouncedSearchUsersForDelegation(delegationSearchQuery);
        return () => {
            debouncedSearchUsersForDelegation.cancel();
            delegationRequestVersion.current += 1;
        };
    }, [delegationSearchQuery, debouncedSearchUsersForDelegation]);

    return (
        <div className="min-h-screen bg-gray-50">
            <OrganizationContextSwitcher></OrganizationContextSwitcher>
            <UsersToolbar
                query={searchQuery}
                filters={searchFilters}
                canImportUsers={canImportUsers}
                canCreateUsers={canCreateUsers}
                onQueryChange={setSearchQuery}
                onFiltersChange={(filters) => {
                    setIncludeStaff(filters.includeStaff);
                    setIncludeStudents(filters.includeStudents);
                    setIncludeParents(filters.includeParents);
                    setIncludeGuests(filters.includeGuests);
                }}
                onImport={toggleImportUsersModal}
                onCreate={toggleCreateUserModal}
            />
            <UsersList
                users={users}
                query={searchQuery}
                isLoading={isSearching}
                error={searchError}
                canViewMetadata={canViewMetadata}
                canResetPassword={canResetPassword}
                canEditUser={canEditUser}
                canSendMessage={canSendMessage}
                canImpersonate={canImpersonate}
                isSuperAdmin={isSuperAdmin}
                canManageMemberships={canManageMemberships}
                onShowDetails={(user) => showMetadata(user)()}
                onResetPassword={(user) => startResetPassword(user)()}
                onEdit={(user) => startUpdateUser(user)()}
                onSendMessage={(user) => startSendMessage(user)()}
                onCreateLoginLink={(user) => startLoginLink(user)()}
                onManageClaims={(user) => startManageClaims(user)()}
                onArchive={(user) => startLifecycleAction("archive", user)}
                onDelete={(user) => startLifecycleAction("delete", user)}
            />

            <UserDetailsModal
                isOpen={showMetadataModal}
                user={selectedUser}
                resolvedMetadata={selectedUserMetadata}
                organizationId={organizationId}
                canManageUserPermissions={canManageUserPermissions}
                delegatedAccess={delegatedAccessData}
                onClose={toggleMetadataModal}
                onShowRelated={(key, value) => startShowRelated(selectedUser, key, value)()}
                onStartAddDelegation={startAddDelegation}
                onRemoveDelegation={removeDelegation}
            />

            <UserLifecycleModal
                action={lifecycleAction}
                user={lifecycleUser}
                onClose={closeLifecycleModal}
                onCompleted={completeLifecycleAction}
            />

            <ImportUsersModal
                isOpen={showImportUsersModal}
                organizationId={organizationId}
                onClose={toggleImportUsersModal}
                onImported={refreshUsers}
            />

            <UserFormModal
                mode="create"
                isOpen={showCreateUserModal}
                organizationId={organizationId}
                onClose={() => setShowCreateUserModal(false)}
                onSaved={refreshUsers}
            />
            <UserFormModal
                mode="edit"
                isOpen={showUpdateUserModal}
                user={selectedUser}
                organizationId={organizationId}
                onClose={() => setShowUpdateUserModal(false)}
                onSaved={refreshUsers}
            />

            <UserActionModals
                user={selectedUser}
                organizationId={organizationId}
                resetPasswordOpen={showResetPasswordModal}
                messageOpen={showCreateMessageModal}
                loginLinkOpen={showLoginLinkModal}
                claimsOpen={showManageClaimsModal}
                onCloseResetPassword={() => setShowResetPasswordModal(false)}
                onCloseMessage={() => setShowCreateMessageModal(false)}
                onCloseLoginLink={() => setShowLoginLinkModal(false)}
                onCloseClaims={() => setShowManageClaimsModal(false)}
            />

            <RelatedUsersModal
                isOpen={showRelatedUsersModal}
                metadataKey={selectedMetadataKey}
                metadataValue={selectedMetadataValue}
                users={relatedUsers}
                onClose={toggleRelatedUsersModal}
                onSelectUser={(user) => {
                    toggleRelatedUsersModal();
                    showMetadata(user)();
                }}
            />
            <AddDelegationModal
                isOpen={showAddDelegationModal}
                query={delegationSearchQuery}
                results={delegationSearchResults}
                selectedUser={selectedDelegatee}
                rules={newDelegationRules}
                onQueryChange={setDelegationSearchQuery}
                onSelectUser={setSelectedDelegatee}
                onRulesChange={setNewDelegationRules}
                onClose={() => setShowAddDelegationModal(false)}
                onAdd={addDelegation}
            />
        </div>
    );
};
